/**
 * Community API Transforms
 *
 * Converts backend DTOs (community-types-api.ts) → frontend types (types.ts).
 * Components never see backend shapes; all adaptation happens here.
 */

import type {
  CommunityUser,
  CommunityPost,
  CommunityComment,
  CommunityBoard,
  AbilityType,
} from "./types";
import type {
  ApiUserInfo,
  ApiUserStats,
  ApiPostCardItem,
  ApiPostDetailResp,
  ApiCommentItem,
  ApiSectionGroup,
} from "./community-types-api";
import { sectionIdToSlug } from "./boards";
import { COMMUNITY_BOARDS } from "./boards";
import { generateAvatarDataUri } from "@/lib/avatar";

// ── Post type mapping ──
// Backend uses "agent_team" / "article", frontend uses "agent-team" / "discussion"

const POST_TYPE_TO_ABILITY: Record<string, AbilityType> = {
  skill: "skill",
  workflow: "workflow",
  tool: "tool",
  agent: "agent",
  agent_team: "agent-team",
  article: "discussion",
};

const ABILITY_TO_POST_TYPE: Record<AbilityType, string> = {
  skill: "skill",
  workflow: "workflow",
  tool: "tool",
  agent: "agent",
  "agent-team": "agent_team",
  discussion: "article",
};

export function mapPostType(postType: string): AbilityType {
  return POST_TYPE_TO_ABILITY[postType] ?? "discussion";
}

export function mapAbilityType(abilityType: AbilityType): string {
  return ABILITY_TO_POST_TYPE[abilityType] ?? "article";
}

// ── User transform ──

function clampLevel(level: number): 1 | 2 | 3 | 4 | 5 | 6 {
  return Math.max(1, Math.min(6, level || 1)) as 1 | 2 | 3 | 4 | 5 | 6;
}

/** 检测是否为系统生成的 ID（如 "user_4668_1..." 或 "用户4569"） */
function isSystemId(name: string): boolean {
  return /^user[_-]\d+/i.test(name) || /^用户\d+$/.test(name);
}

/** 系统 ID → "小虾米" + 尾号5位，如 "小虾米68123" */
function friendlyName(id: string): string {
  const digits = id.replace(/\D/g, "");
  const tail = digits.slice(-5).padStart(5, "0");
  return `小虾米${tail}`;
}

function resolveDisplayName(
  nick: string | undefined,
  username: string | undefined,
  id: string,
): string {
  if (nick?.trim() && !isSystemId(nick.trim())) return nick.trim();
  if (username?.trim() && !isSystemId(username.trim())) return username.trim();
  return friendlyName(id);
}

export function transformUser(
  api: ApiUserInfo,
  stats?: ApiUserStats,
  extra?: { isFollowed?: boolean },
): CommunityUser {
  const displayName = resolveDisplayName(
    api.nick,
    api.username,
    String(api.id),
  );
  return {
    id: String(api.id),
    displayName,
    handle: api.handle || undefined,
    avatar: api.avatar || generateAvatarDataUri(displayName),
    bio: api.bio || "",
    isVerified: false,
    creatorLevel: clampLevel(api.level),
    joinedAt: api.createdAt,
    stats: {
      posts: stats?.postCount ?? 0,
      followers: stats?.followerCount ?? 0,
      following: stats?.followingCount ?? 0,
      totalLikes: stats?.likeCount ?? 0,
      totalCopies: stats?.copyCount ?? 0,
      totalTips: 0,
    },
    ...(extra?.isFollowed != null && { isFollowed: extra.isFollowed }),
  };
}

// ── Post transform ──

export function transformPostCard(card: ApiPostCardItem): CommunityPost {
  const { post, author, tags, isFollowedAuthor } = card;
  const safeTags = tags ?? [];
  return {
    id: String(post.id),
    title: post.title,
    description: post.content,
    coverImage: post.coverImage || undefined,
    aiMessage: post.aiMessage || undefined,
    author: transformUser(
      author,
      undefined,
      isFollowedAuthor != null ? { isFollowed: isFollowedAuthor } : undefined,
    ),
    abilityType: mapPostType(post.postType),
    boardSlug: sectionIdToSlug(String(post.sectionId)),
    tags: safeTags.map((t) => t.name),
    stats: {
      likes: post.likeCount,
      comments: post.commentCount,
      copies: post.copyCount,
      favorites: post.favoriteCount ?? 0,
      tips: 0,
    },
    isLiked: card.isLiked ?? post.isLiked ?? false,
    isFavorited: card.isFaved ?? post.isFaved ?? false,
    createdAt: post.createdAt,
    updatedAt: post.updatedAt,
  };
}

export function transformPostDetail(resp: ApiPostDetailResp): CommunityPost {
  return transformPostCard({
    post: resp.post,
    author: resp.author,
    tags: resp.tags,
    isFollowedAuthor: resp.isFollowedAuthor,
    isLiked: resp.isLiked,
    isFaved: resp.isFaved,
  });
}

// ── Comment transform ──

export function transformComment(
  api: ApiCommentItem,
  /** 父评论的 postId（子评论后端可能不返回，需从父级继承） */
  inheritPostId?: string,
  /** 父评论的作者名（用于子评论显示"回复 xxx"） */
  parentAuthorName?: string,
): CommunityComment {
  const postId = String(api.postId || inheritPostId || "");
  const authorDisplayName = resolveDisplayName(
    api.author.nick,
    api.author.username,
    String(api.author.id),
  );
  return {
    id: String(api.id),
    postId,
    author: transformUser(api.author),
    content: api.content,
    likes: api.likes,
    isLiked: api.isLiked,
    createdAt: api.createdAt,
    parentAuthorName,
    replies: api.replies?.map((reply) =>
      transformComment(reply, postId, authorDisplayName),
    ),
    replyTotal: api.replyTotal,
  };
}

// ── Section → Board transform ──

export function transformSections(groups: ApiSectionGroup[]): CommunityBoard[] {
  const boards: CommunityBoard[] = [];
  for (const group of groups) {
    for (const section of group.sections) {
      const slug = sectionIdToSlug(String(section.id));
      // Supplement with static board data (slogan, description, color, icon, abilityType)
      const staticBoard = COMMUNITY_BOARDS.find((b) => b.slug === slug);
      boards.push({
        slug,
        name: section.name,
        slogan: staticBoard?.slogan ?? "",
        description: staticBoard?.description ?? "",
        icon: section.icon || staticBoard?.icon || "MessageCircle",
        category: mapSectionCategory(section.category, staticBoard?.category),
        postCount: section.postCount,
        color: staticBoard?.color ?? "gray",
        abilityType: staticBoard?.abilityType,
      });
    }
  }
  return boards;
}

function mapSectionCategory(
  apiCategory: string,
  fallback?: "scenario" | "type" | "special",
): "scenario" | "type" | "special" {
  // Backend uses "theme" for scenario boards
  if (apiCategory === "theme") return "scenario";
  if (
    apiCategory === "scenario" ||
    apiCategory === "type" ||
    apiCategory === "special"
  ) {
    return apiCategory;
  }
  return fallback ?? "scenario";
}
